import { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import Icons from '../../../../../components/Common/Icons/Icons';
import { cn } from '../../../../../utils/styles';
import { IAccordionData } from '../../../Common/AccordionTable';
import { GuidanceViewPdfButton } from '../../Dashboard/Data';

function formatDateShort(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  return date.toLocaleDateString('en-US', options);
}

function getDateForMonth(year: number, monthIndex: number, day: number): string {
  const date = new Date(year, monthIndex, day);

  return formatDateShort(date);
}

const monthsConfig = [
  {
    key: 'jan',
    day: 15,
    monthIndex: 0,
    form: '1098',
  },
  {
    key: 'feb',
    day: 10,
    monthIndex: 1,
    form: '1099-B',
  },
  {
    key: 'mar',
    day: 5,
    monthIndex: 2,
    form: '1099-C',
  },
  {
    key: 'apr',
    day: 20,
    monthIndex: 3,
    form: '1099-DIV',
  },
  {
    key: 'may',
    day: 15,
    monthIndex: 4,
    form: '1099-INT',
  },
  {
    key: 'jun',
    day: 25,
    monthIndex: 5,
    form: '1099-MISC',
  },
  {
    key: 'jul',
    day: 12,
    monthIndex: 6,
    form: '1099-Q',
  },
  {
    key: 'aug',
    day: 8,
    monthIndex: 7,
    form: '1099-OID',
  },
  {
    key: 'sep',
    day: 17,
    monthIndex: 8,
    form: '1099-R',
  },
  {
    key: 'oct',
    day: 3,
    monthIndex: 9,
    form: '1099-SA',
  },
  {
    key: 'nov',
    day: 11,
    monthIndex: 10,
    form: '5498',
  },
  {
    key: 'dec',
    day: 31,
    monthIndex: 11,
    form: '5498-ESA',
  },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth(); // 0-11

function generateTaxesData(year: number): ITaxesData[] {
  const availableMonths = year === currentYear ? monthsConfig.filter(m => m.monthIndex < currentMonth) : monthsConfig;

  return availableMonths.map((config, index) => ({
    id: String(index + 1),
    date: getDateForMonth(year, config.monthIndex, config.day),
    form: config.form,
  }));
}

export interface ITaxesData {
  id: string;
  date: string;
  form: string;
  type?: 'Payroll' | string;
  pdfSrc?: string;
}

export const SEARCH_SUGGESTIONS = ['Payroll'];

export const taxesCol: ColumnDef<ITaxesData>[] = [
  {
    header: 'Date',
    accessorKey: 'date',
    cell: ({ row }) => (
      <div className="flex items-center gap-[10px]">
        <div className={cn('w-[8px] h-[8px] rounded bg-addition8 uppercase')} />
        {row.getValue('date')}
      </div>
    ),
  },
  {
    header: 'Form',
    accessorKey: 'form',
  },
  {
    id: 'actions',
    accessorKey: '',
    header: 'Actions',
    size: 450,
    cell: ({ row }) => {
      const pdfSrc = row.original.pdfSrc || '/guidance/pdfs/RISE-1098-Compliant.pdf';

      return <GuidanceViewPdfButton src={pdfSrc} />;
    },
  },
];

const taxesData2026 = generateTaxesData(currentYear);
const taxesData2025 = generateTaxesData(currentYear - 1);
const taxesData2024 = generateTaxesData(currentYear - 2);
const taxesData2023 = generateTaxesData(currentYear - 3);

const lastAvailableMonth = taxesData2026[taxesData2026.length - 1]?.date || getDateForMonth(currentYear, 0, 15);

const taxesData2026WithSearch = [
  ...taxesData2026,
  {
    id: 'ach-1',
    date: lastAvailableMonth,
    form: '1098',
    type: 'Payroll',
    pdfSrc: '/guidance/pdfs/RISE-1098-Compliant.pdf',
  },
];

export const taxesAccordion: IAccordionData<ITaxesData>[] = [
  {
    id: '1',
    label: `Taxes from ${currentYear}`,
    year: currentYear,
    data: taxesData2026WithSearch,
  },
  {
    id: '2',
    label: `Taxes from ${currentYear - 1}`,
    year: currentYear - 1,
    data: taxesData2025,
  },
  {
    id: '3',
    label: `Taxes from ${currentYear - 2}`,
    year: currentYear - 2,
    data: taxesData2024,
  },
  {
    id: '4',
    label: `Taxes from ${currentYear - 3}`,
    year: currentYear - 3,
    data: taxesData2023,
  },
];
